import { ColumnDef } from '@tanstack/react-table';
import { Lesson, Resubmission } from '@/entities/Courses';
import { formatDateTime, formatDateTimeStored } from '@/shared/lib/utils/formatDate';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { Svg } from '@/shared/ui/Svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import { SessionActionsMenu } from '../ui/components/SessionActionsMenu';

type Session = Lesson | Resubmission;
export type ModuleSessionTableRow =
  | (Lesson & { _kind: 'lesson' })
  | (Resubmission & { _kind: 'resubmission' });

const formatBadgeMap: Record<string, { bg: string; text: string; label: string }> = {
  online: { bg: '#CFF7D3', text: '#14AE5C', label: 'Online' },
  offline: { bg: '#DBEAFE', text: '#1447E6', label: 'Offline' },
  meeting: { bg: '#FFE8A3', text: '#BF6A02', label: 'Meeting' },
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins} min`;
  }
  return `${mins} min`;
};


export const sessionsColumns = (): ColumnDef<ModuleSessionTableRow>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        uniqueKey="all-sessions-select"
        name="all-sessions"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        uniqueKey={`session-select-${row.original.id}`}
        name="session"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    size: 68,
  },
  {
    header: 'Nº',
    accessorKey: 'order',
    cell: ({ row }) => <span className="text-black-headline">{row.original.order}</span>,
    size: 63,
  },
  {
    header: 'Session',
    accessorKey: 'title',
    cell: ({ row }) => (
      <div className="flex flex-col max-w-[260px]">
        <span className="block truncate text-black-headline" title={row.original.title}>
          {row.original.title}
        </span>
        {row.original.description && (
          <span
            className="block truncate text-grey-secondary text-sm"
            title={row.original.description}
          >
            {row.original.description}
          </span>
        )}
      </div>
    ),
    size: 169,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Format</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'format',
    cell: ({ row }) => {
      const key = String(row.original.format || 'offline').toLowerCase();
      const formatBadge = formatBadgeMap[key] || formatBadgeMap.offline;
      return (
        <span
          className="inline-flex h-6 items-center rounded-[6px] px-2 text-xs font-semibold"
          style={{ backgroundColor: formatBadge.bg, color: formatBadge.text }}
        >
          {formatBadge.label}
        </span>
      );
    },
    size: 144,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Start Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'scheduledAt',
    cell: ({ row }) => {
      const dateString = row.original.scheduledAt;
      if (!dateString) {
        return <span className="text-grey-secondary">—</span>;
      }
      const scheduledActivity = formatDateTimeStored(dateString);
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{scheduledActivity?.date}</span>
          <span className="text-grey-secondary text-sm">{scheduledActivity?.time}</span>
        </div>
      );
    },
    size: 130,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Progress</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'progress',
    cell: ({ row }) => {
      const progress = 'progress' in row.original ? row.original.progress : undefined;
      return progress !== undefined ? (
        <span className="text-black-headline">{progress}%</span>
      ) : (
        <span className="text-grey-secondary">—</span>
      );
    },
    size: 144,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Duration</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'duration',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatDuration(row.original.duration)}</span>
    ),
    size: 144,
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Last Activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'lastActivity',
    cell: ({ row }) => {
      const dateString = row.original.lastActivity || row.original.scheduledAt;
      if (!dateString) {
        return <span className="text-grey-secondary">—</span>;
      }
      const lastActivity = formatDateTime(dateString);
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{lastActivity?.date}</span>
          <span className="text-grey-secondary text-sm">{lastActivity?.time}</span>
        </div>
      );
    },
    size: 144,
  },
  {
    id: 'tools',
    cell: ({ row, table }) => {
      const meta = table.options.meta as
        | {
            onView?: (session: Session) => void;
            onMarkAttendance?: (session: Session) => void;
            onEdit?: (session: Session) => void;
            onDuplicate?: (session: Session) => void;
            onDelete?: (session: Session) => void;
          }
        | undefined;
      return (
        <SessionActionsMenu
          session={row.original}
          onView={meta?.onView}
          onMarkAttendance={meta?.onMarkAttendance}
          onEdit={meta?.onEdit}
          onDuplicate={meta?.onDuplicate}
          onDelete={meta?.onDelete}
        />
      );
    },
    size: 68,
  },
];
